import toast from "react-hot-toast";
import { handleFetchError } from "./errorhandler";
import { t } from "i18next";
import useAuthStore from "../store/Auth";

// Track if we've already shown the no internet connection toast
let hasShownNoInternetToast = false;

interface FetchData {
  noBaseUrl?: boolean;
  baseUrl?: string;
  url: string;
  method?: string;
  body?: BodyInit | null;
  token?: string | null;
  image?: boolean;
  abortSignal?: AbortSignal | null;
  credentials?: RequestCredentials;
  isFormData?: boolean;
  isFormUrlEncoded?: boolean;
  isReturnBlob?: boolean;
}

export const fetchUtil = async (data: FetchData) => {
  const {
    baseUrl,
    noBaseUrl = false,
    url,
    method = "GET",
    body = null,
    token = null,
    image = false,
    abortSignal = null,
    credentials = "same-origin",
    isFormData = false,
    isFormUrlEncoded = false,
    isReturnBlob = false,
  } = data;
  let headers: Record<string, string> = {};

  if (!image && !isFormData) {
    headers = { "Content-Type": "application/json" };
  }

  if (isFormData && !isFormUrlEncoded) {
    headers = {
      Accept: "*/*",
      "Accept-Encoding": "gzip, deflate, br",
    };
  }

  if (isFormData && isFormUrlEncoded) {
    headers = {
      Accept: "*/*",
      "Accept-Encoding": "gzip, deflate, br",
      "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
    };
  }

  if (token) {
    headers = { ...headers, Authorization: `Bearer ${token}` };
  }

  const requestUrl = `${!noBaseUrl
    ? !baseUrl
      ? process.env.REACT_APP_API_BASE_URL
      : baseUrl
    : ""
    }${url}`;

  const requestInit: RequestInit = {
    method,
    headers,
    body,
    credentials,
    ...(abortSignal && { signal: abortSignal }),
  };

  return fetch(requestUrl, requestInit)
    .then((res) =>
      handleFetchError(res, url, isReturnBlob, async () => {
        // Re-read token from store after refresh
        const { token: newToken } = useAuthStore.getState();
        const retryInit = {
          ...requestInit,
          headers: {
            ...headers,
            ...(newToken && { Authorization: `Bearer ${newToken}` }),
          },
        };
        return fetch(requestUrl, retryInit);
      })
    )
    .catch((error) => {
      // Handle network connectivity issues
      if (
        error.name === "TypeError" &&
        error.message.includes("Failed to fetch")
      ) {
        const networkError = new Error("No internet connection");
        (networkError as any).status = 0;
        (networkError as any).error = "ERR_INTERNET_DISCONNECTED";
        (networkError as any).result = null;

        // Only show toast if we haven't shown it already
        if (!hasShownNoInternetToast) {
          toast.error(t("noInternetConnection"));
          hasShownNoInternetToast = true;

          // Reset the flag after 5 seconds to allow showing again if needed
          setTimeout(() => {
            hasShownNoInternetToast = false;
          }, 8000);
        }

        throw networkError;
      }

      // Re-throw other errors
      throw error;
    });
};
